// src/utils/rapDetailExport.js
// Export RAP Detail to PDF and Excel - Only used items with RAP values
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

const formatCurrency = (value) => {
  if (!value && value !== 0) return '-';
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatVolume = (value) => {
  if (!value && value !== 0) return '0';
  return new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(value);
};

const collectRAPItems = (rapData, categories) => {
  const allItems = [];
  let itemNo = 1;
  
  const processCategory = (category) => {
    const categoryData = rapData[category.id];
    if (categoryData && categoryData.items) {
      categoryData.items
        .filter(item => !item.isNotUsed)
        .forEach(item => {
          allItems.push({
            no: itemNo++,
            categoryCode: category.categoryCode,
            categoryName: category.categoryName,
            ...item
          });
        });
    }
    
    if (category.children && category.children.length > 0) {
      category.children.forEach(child => processCategory(child));
    }
  };
  
  categories.forEach(category => processCategory(category));
  
  return allItems;
};

export const exportRAPDetailToPDF = (tower, rapData, categories, rapType, priceCoefficient) => {
  const doc = new jsPDF('landscape');
  
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text(`RAP DETAIL - ${rapType}`, 14, 15);
  
  doc.setFontSize(12);
  doc.setFont(undefined, 'normal');
  doc.text(`${tower.siteName} (${tower.siteId})`, 14, 23);
  
  doc.setFontSize(10);
  doc.text(`Province: ${tower.provinsi} | Coefficient: ×${priceCoefficient.toFixed(4)}`, 14, 30);
  doc.text(`Generated: ${new Date().toLocaleDateString('id-ID')}`, 14, 36);
  
  const items = collectRAPItems(rapData, categories);
  
  if (items.length === 0) {
    doc.setFontSize(12);
    doc.text('No items to export', 14, 50);
    doc.save(`RAP_Detail_${tower.siteId}_${rapType}_${new Date().toISOString().split('T')[0]}.pdf`);
    return;
  }
  
  const tableData = items.map(item => [
    item.no,
    item.itemDescription,
    item.unit,
    formatVolume(item.volume),
    formatCurrency(item.rapUnitPrice),
    formatCurrency(item.rapTotalPrice),
    item.trCode || '-',
    item.postingType || '-'
  ]);
  
  doc.autoTable({
    head: [['NO', 'DESCRIPTION', 'UNIT', 'VOLUME', 'RAP UNIT PRICE', 'RAP TOTAL PRICE', 'TR CODE', 'POSTING']],
    body: tableData,
    startY: 42,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold', halign: 'center' },
    columnStyles: {
      0: { cellWidth: 15, halign: 'center' },
      1: { cellWidth: 85 },
      2: { cellWidth: 20, halign: 'center' },
      3: { cellWidth: 25, halign: 'right' },
      4: { cellWidth: 35, halign: 'right' },
      5: { cellWidth: 40, halign: 'right' },
      6: { cellWidth: 30, halign: 'center' },
      7: { cellWidth: 25, halign: 'center' }
    }
  });
  
  const grandTotal = items.reduce((sum, item) => sum + (parseFloat(item.rapTotalPrice) || 0), 0);
  
  const finalY = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text(`GRAND TOTAL: ${formatCurrency(grandTotal)}`, 14, finalY);
  
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.getWidth() / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }
  
  doc.save(`RAP_Detail_${tower.siteId}_${rapType}_${new Date().toISOString().split('T')[0]}.pdf`);
};

export const exportRAPDetailToExcel = (tower, rapData, categories, rapType, priceCoefficient) => {
  const items = collectRAPItems(rapData, categories);
  
  if (items.length === 0) {
    alert('No items to export');
    return;
  }
  
  const excelData = [];
  
  excelData.push([`RAP DETAIL - ${rapType}`]);
  excelData.push([`${tower.siteName} (${tower.siteId})`]);
  excelData.push([`Province: ${tower.provinsi} | Coefficient: ×${priceCoefficient.toFixed(4)}`]);
  excelData.push([`Generated: ${new Date().toLocaleDateString('id-ID')}`]);
  excelData.push([]);
  excelData.push(['NO', 'DESCRIPTION', 'UNIT', 'VOLUME', 'RAP UNIT PRICE', 'RAP TOTAL PRICE', 'TR CODE', 'POSTING']);
  
  items.forEach(item => {
    excelData.push([
      item.no,
      item.itemDescription,
      item.unit,
      parseFloat(item.volume) || 0,
      parseFloat(item.rapUnitPrice) || 0,
      parseFloat(item.rapTotalPrice) || 0,
      item.trCode || '',
      item.postingType || ''
    ]);
  });
  
  const grandTotal = items.reduce((sum, item) => sum + (parseFloat(item.rapTotalPrice) || 0), 0);
  excelData.push([]);
  excelData.push(['', '', '', '', 'GRAND TOTAL:', grandTotal, '', '']);
  
  const ws = XLSX.utils.aoa_to_sheet(excelData);
  ws['!cols'] = [
    { wch: 5 }, { wch: 50 }, { wch: 8 }, { wch: 12 }, { wch: 18 }, { wch: 20 }, { wch: 15 }, { wch: 12 }
  ];
  
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'RAP Detail');
  XLSX.writeFile(wb, `RAP_Detail_${tower.siteId}_${rapType}_${new Date().toISOString().split('T')[0]}.xlsx`);
};